'use client';

import * as React from 'react';
import { format } from 'date-fns';
import { fr, enUS, es, it, type Locale } from 'date-fns/locale';
import { useLocale } from 'next-intl';
import { Calendar as CalendarIcon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { cn } from '@/lib/utils';

interface DatePickerProps {
  value?: Date | string;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  maxDate?: Date;
  minDate?: Date;
  disabled?: boolean;
}

const localeMap: Record<string, Locale> = {
  fr,
  en: enUS,
  es,
  it,
};

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Sélectionner une date',
  className,
  maxDate,
  minDate,
  disabled = false,
}) => {
  const locale = useLocale();
  const currentLocale = localeMap[locale] || fr;
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    value ? (typeof value === 'string' ? new Date(value) : value) : undefined
  );
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (value) {
      setSelectedDate(typeof value === 'string' ? new Date(value) : value);
    }
  }, [value]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    onChange?.(date);
    if (date) {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100',
          'hover:bg-gray-50 dark:hover:bg-gray-700',
          'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
      >
        <span className={cn('flex items-center gap-2', !selectedDate && 'text-gray-500 dark:text-gray-400')}>
          <CalendarIcon className="h-4 w-4" />
          {selectedDate ? format(selectedDate, 'dd/MM/yyyy', { locale: currentLocale }) : placeholder}
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            locale={currentLocale}
            disabled={(date) => {
              if (maxDate && date > maxDate) return true;
              if (minDate && date < minDate) return true;
              return false;
            }}
            className="p-3"
            classNames={{
              months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
              month: 'space-y-4',
              caption: 'flex justify-center pt-1 relative items-center',
              caption_label: 'text-sm font-medium text-gray-900 dark:text-gray-100',
              nav: 'space-x-1 flex items-center',
              nav_button: cn(
                'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
                'border border-gray-300 dark:border-gray-600 rounded',
                'hover:bg-gray-100 dark:hover:bg-gray-700'
              ),
              nav_button_previous: 'absolute left-1',
              nav_button_next: 'absolute right-1',
              table: 'w-full border-collapse space-y-1',
              head_row: 'flex',
              head_cell: 'text-gray-500 dark:text-gray-400 rounded-md w-9 font-normal text-[0.8rem]',
              row: 'flex w-full mt-2',
              cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-gray-100 dark:[&:has([aria-selected].day-outside)]:bg-gray-800 [&:has([aria-selected])]:bg-gray-100 dark:[&:has([aria-selected])]:bg-gray-800 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
              day: cn(
                'h-9 w-9 p-0 font-normal aria-selected:opacity-100',
                'hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md',
                'text-gray-900 dark:text-gray-100'
              ),
              day_selected: 'bg-gold text-white hover:bg-gold-dark hover:text-white focus:bg-gold focus:text-white font-semibold',
              day_today: 'bg-gray-100 dark:bg-gray-700 font-semibold',
              day_outside: 'text-gray-400 dark:text-gray-500 opacity-50',
              day_disabled: 'text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed',
              day_range_middle: 'aria-selected:bg-gray-100 dark:aria-selected:bg-gray-800 aria-selected:text-gray-900 dark:aria-selected:text-gray-100',
              day_hidden: 'invisible',
            }}
          />
        </div>
      )}
    </div>
  );
};

